import {corruptedFile, eye} from 'assets/images/images';
import {EmptyState, Loader, SearchInput, TableLayout} from 'components';
import CardSummary from 'components/CardSummary';
import {getStatusTextAndStyles} from 'helpers/getStatusTextAndStyles';
import React, {useMemo, useState} from 'react';
import InvoiceDetailsModal from './components/InvoiceDetailsModal';
import InvoiceLayout from './components/InvoiceLayout';
import {useQuery} from 'react-query';
import {fetchInvoiceOverview, fetchInvoices} from 'requests/queries/veldro';
import {toast} from 'react-toastify';
import moment from 'moment';

function SentInvoices() {
  const [invoiceDetails, setInvoiceDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const toggleInvoiceModal = invoice => {
    setSelectedInvoice(invoice);
    setInvoiceDetails(prev => !prev);
  };

  const params = useMemo(
    () => ({
      business_id: '70e2ecd8-380f-48c6-b42b-d83a5e55e590',
    }),
    [],
  );

  const {data, isLoading} = useQuery(
    ['all-invoices', {params}],
    () => fetchInvoices({params}),
    {
      onError: err => {
        console.error('Error fetching:', err);
        toast.error('Failed to load  data.');
      },
    },
  );

  const {data: overviewData, isLoading: loadingOverview} = useQuery(
    ['invoice-overview', {params}],
    () => fetchInvoiceOverview({params}),
    {
      onError: err => {
        console.error('Error fetching:', err);
        toast.error('Failed to load  data.');
      },
    },
  );

  const tableHeader = [
    {label: 'Business name', key: 'name'},
    {label: 'IRN', key: 'irn'},
    {label: 'Issue Date', key: 'issue_date'},
    {label: 'Due Date', key: 'due_date'},
    {label: 'Amount', key: 'amount'},
    {label: 'Status', key: 'status'},
    {label: 'Action', key: 'action'},
  ];

  const {totalInvoices, pendingInvoices, completedInvoices} = useMemo(() => {
    let total = 0;
    let pending = 0;
    let completed = 0;

    if (overviewData?.total_count) {
      overviewData.total_count.forEach(item => {
        total += item.count;
        if (item.invoice_status === 'SIGNED') {
          pending += item.count;
        } else {
          completed += item.count;
        }
      });
    }

    return {
      totalInvoices: total,
      pendingInvoices: pending,
      completedInvoices: completed,
    };
  }, [overviewData]);

  return (
    <InvoiceLayout>
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput placeholder="Search here" />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4 h-full my-6">
        <CardSummary
          title="Total Invoices"
          body={totalInvoices}
          loading={loadingOverview}
        />
        <CardSummary
          title="Completed Invoices"
          body={completedInvoices}
          loading={loadingOverview}
        />
        <CardSummary
          title="Pending Invoices"
          body={pendingInvoices}
          loading={loadingOverview}
        />
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <Loader height={35} />
        </div>
      ) : (
        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              {tableHeader.map(({label, key}) => (
                <th
                  key={key}
                  className={`${
                    label === 'Business name' ? 'pl-4 sm:pl-6 xl:pl-8' : 'pl-0'
                  } h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap`}
                >
                  <div className="flex items-end gap-1 cursor-pointer">
                    <span>{label}</span>
                  </div>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody className="text-sm bg-white">
            {data?.data?.length > 0 ? (
              data.data.map(item => (
                <tr
                  key={item.id}
                  onClick={() => toggleInvoiceModal(item)}
                  className="border-[0.5px] border-[#E4E3EC] cursor-pointer"
                >
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {item.business_name || 'Test Business'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {item.irn}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(item.issue_date).format('Do MMM, YYYY')}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(item.due_date).format('Do MMM, YYYY')}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    ₦{Number(item.amount).toLocaleString()}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <span
                      className={`uppercase py-1 px-2 text-[10px] rounded-full ${
                        getStatusTextAndStyles(
                          item.invoice_status?.toLowerCase(),
                        ).styles
                      }`}
                    >
                      {getStatusTextAndStyles(
                        item.invoice_status?.toLowerCase(),
                      ).text || '-'}
                    </span>
                  </td>
                  <td className="p-4 pl-0 whitespace-nowrap">
                    <button title="View" className="h-4 aspect-square">
                      <img src={eye} alt="View" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <EmptyState
                  height={35}
                  body={'No sent Invoices'}
                  src={corruptedFile}
                  noBtn
                />
              </tr>
            )}
          </tbody>
        </TableLayout>
      )}
      <InvoiceDetailsModal
        show={invoiceDetails}
        onClose={() => setInvoiceDetails(false)}
        invoice={selectedInvoice}
      />{' '}
    </InvoiceLayout>
  );
}

export default SentInvoices;
